/**
 * Created by Kotkin on 29.05.2017.
 */
import React, { PureComponent, Children, cloneElement, createElement } from 'react';
import PropTypes from 'prop-types';
import Bem from 'bemHelper';
import { v4 as uuid } from 'uuid';
import getUid from 'uniqueId';
import { Helmet } from 'react-helmet';
import objPath from 'object-path';
import assign from 'lodash/assign';
import forEach from 'lodash/forEach';
import map from 'lodash/map';
import sensor from 'components/sensor';
import DragScroll from 'components/drag-scroll';
import { iconTypes } from 'components/icon';

import TabButton from './tab-button';

import 'styles/base/components/tab-control/_tab-control.scss';

const classes = new Bem('tabControl');

/**
 * = TabControl Component =
 *
 * == Example ==
 * <TabControl>
 *   <tab onSelected={() => ...}>
 *     <tabIcon>
*         <Icon {...classes('icon" type={icon type} />
 *     </tabIcon>
 *     <tabTitle>{title}</tabTitle>
 *     <tabLabel>{label}</tabLabel>
 *     <tabContent>
 *       {tab Content}
 *     </tabContent>
 *   </tab>
 *   <aside>
 *     {aside content}
 *   </aside>
 * </TabControl>
 *
 * == Note ==
 * TabTitle - is Required!
 *
 * === Props ===
 *
 */

@sensor
@getUid
export default class TabControl extends PureComponent {
  static propTypes = {
    className: PropTypes.string,
    children: PropTypes.node.isRequired,
    tabOrange: PropTypes.bool,
    paginatorCount: PropTypes.number,
    tabContentScroll: PropTypes.bool
  };

  static isFullStyle(item) {
    if (!Array.isArray(item)) {
      if (item.type === 'tabTitle') {
        return { button: item };
      }
      return false;
    }

    let button = false;
    let icon = false;
    let after = false;
    let content = false;
    forEach(item, (it) => {
      if (it.type === 'tabTitle') button = it;
      if (it.type === 'tabIcon') icon = it;
      if (it.type === 'tabLabel') after = it;
      if (it.type === 'tabContent') content = createElement('div', { ...it.props });
    });

    if (!button) return false;

    return { button, icon, after, content };
  }

  static isInfoBlockStyle(item) {
    if (Array.isArray(item)) return false;
    const infoBlock = item;
    if (!infoBlock) return false;
    return { infoBlock };
  }

  static renderAsideBlock(block, tabId, paginatorCount) {
    let children = [];
    if (paginatorCount) {
      const countElem = (
        <span {...classes('text')}>Найдено {paginatorCount}</span>
      );
      children = map(block.props.children, (child, key) => {
        if (child) {
          const { dataCount = false, ...restProps } = child.props;
          if (dataCount) {
            return createElement(child.type, {
              ...restProps, key, children: countElem
            });
          }
          return cloneElement(child, { key });
        }
      });
    } else {
      children = block.props.children;
    }

    return (
      <div
        {...classes('aside', null, block.props.className)}
        key={tabId}
        children={children}
      />
    );
  }

  constructor(props, context) {
    super(props, context);
    this.setCoors = this.setCoors.bind(this);

    this.coors = { clientX: 0, clientY: 0 };
    this._instanceId = (context.getUid || uuid)();
  }

  handleFocusEvent(id) { // eslint-disable-line
    // TODO выбираем таб по нажатию на клавишу TAB
    const tab = document.querySelector(`#${id}`);
    const tabId = tab.querySelector('.tabControl__tabElement').getAttribute('for');
    document.querySelector(`#${tabId}`).click();
    tab.click();
  }

  setCoors(e) {
    e.preventDefault();
    if (!e.changedTouches) return this.coors = { clientX: e.clientX, clientY: e.clientY };
    this.coors = { clientX: e.changedTouches[0].pageX, clientY: e.changedTouches[0].pageY };
  }

  handleTabClick(e, id, index, onSelected) {
    if (this.isMouseRightClick(e)) return false;

    const positionX = e.changedTouches ? e.changedTouches[0].pageX : e.clientX;
    const positionY = e.changedTouches ? e.changedTouches[0].pageY : e.clientY;

    // TODO prevent click on tab li element while dragging
    if (positionX !== 0 &&
      (
        Math.abs(positionX - this.coors.clientX) >= 10 ||
        Math.abs(positionY - this.coors.clientY) >= 10
      )
    ) {
      this.coors = { clientX: positionX, clientY: positionY };
      e.preventDefault();
      return false;
    }

    document.querySelector(`#${id}`).focus();
    if (this.tabActive !== index) {
      this.tabActive = index;
      if (onSelected) onSelected();
    }
  }

  isMouseRightClick(e) { // eslint-disable-line
    let rightClick = false;
    if (!e) return false;
    if (e.which) {
      rightClick = e.which === 3;
    } else if (e.button) {
      rightClick = e.button === 2;
    }

    return rightClick;
  }

  renderTab(tabData, onSelected, tabId, controlId, className, index) {
    return (
      <li
        id={tabId}
        key={tabId}
        {...classes('tab', null, className)}
        role="article"
        aria-hidden
        tabIndex="0"
        onFocus={() => this.handleFocusEvent(tabId)}
        onTouchStart={this.setCoors}
        onTouchEnd={e => this.handleTabClick(e, tabId, index, onSelected)}
        onMouseDown={this.setCoors}
        onMouseUp={e => this.handleTabClick(e, tabId, index, onSelected)}
      >
        <TabButton
          children={tabData}
          id={tabId}
          {...classes('tabElement')}
          htmlFor={controlId}
        />
      </li>
    );
  }

  render() {
    const colorRed = this.sensor.getVariable('red');
    const colorOrange = this.sensor.getVariable('orangeBild');
    const { paginatorCount = null, tabOrange, tabContentScroll } = this.props;
    const colorVal = tabOrange ? colorOrange : colorRed;
    const instanceId = this._instanceId;
    const controlsArr = [];
    const tabsArr = [];
    const asideBlock = [];
    const contentsArr = [];
    const stylesArr = [];
    const getNextIdFn = this.getUid || uuid;
    let selectedIndex = 0;
    Children.map(this.props.children, (item, index) => {
      if (!item) return;
      const {
        children,
        onSelected,
        className,
        manageByVisibility = false,
        selected = false
      } = item.props;

      let tabData = TabControl.isFullStyle(children);
      if (item.type === 'aside') tabData = TabControl.isInfoBlockStyle(item);
      if (!tabData) return;

      const id = `_${getNextIdFn()}`;
      const controlId = `clid_${id}`;
      const tabId = `tbid_${id}`;
      const contentId = `ctid_${id}`;

      if (selected || objPath.get(children, 'props.selected')) {
        this.tabActive = index;
        selectedIndex = index;
      }

      if (item.type !== 'aside') {
        controlsArr.push(
          <input
            key={controlId}
            {...classes('control')}
            id={controlId}
            type="radio"
            name={`tabControl_${instanceId}`}
            defaultChecked={index === selectedIndex}
          />
        );
      }

      if (tabData.infoBlock) {
        asideBlock.push(TabControl.renderAsideBlock(tabData.infoBlock, tabId, paginatorCount));
      } else {
        const buttonData = assign({}, tabData);
        delete buttonData.content;
        tabsArr.push(this.renderTab(buttonData, onSelected, tabId, controlId, className, index));

        stylesArr.push(`#${controlId}:checked ~ div ul #${tabId} #label_${tabId} .tabButton__text {color: ${colorVal};}`);
        stylesArr.push(`#${controlId}:checked ~ div ul #${tabId} #label_${tabId} .tabButton__line {background: ${colorVal};}`);
        stylesArr.push(`#${controlId}:checked ~ div ul #${tabId} #label_${tabId} {cursor: default}`);

        if (tabData.content) {
          contentsArr.push(cloneElement(tabData.content, {
            ...classes('content'),
            key: index,
            id: contentId
          }));
        }
        stylesArr.push(`#${controlId}:checked ~ .tabControl__tabsContent #${contentId} {display: block; visibility: visible; position: relative; overflow: auto; height: auto; z-index: inherit;}`);

        if (manageByVisibility) {
          stylesArr.push(`#${controlId} ~ .tabControl__tabsContent #${contentId} {display: block; position: absolute;}`);
        }
      }
    });

    const combinedStyle = stylesArr.join(' ');

    // TODO Проверить, чтоб стили убирались, когда компонент демонтируется...

    return (
      <div {...classes(null, { tabContentScroll }, this.props.className)}>
        <Helmet style={[{ cssText: combinedStyle }]} />
        {controlsArr}
        <div {...classes('container')}>
          <DragScroll {...classes('draggable')} scrollIconType={iconTypes.fastForward}>
            <ul {...classes('tabs')}>
              {tabsArr}
            </ul>
          </DragScroll>
          {asideBlock}
        </div>
        <div {...classes('tabsContent')}>
          {contentsArr}
        </div>
      </div>
    );
  }
}
